import React from 'react';
import { SlidersHorizontal, ChevronDown, ChevronUp, X, MapPin } from 'lucide-react';
import { ActivityCategory, ACTIVITY_CATEGORIES } from '../../types/activities';
import { budgetCountries } from '../../data/budget-countries';

export interface ActivityFiltersState {
  category?: ActivityCategory;
  countryCode?: string;
  city?: string;
}

interface ActivityFiltersProps {
  onFilterChange: (filters: ActivityFiltersState) => void;
}

export function ActivityFilters({ onFilterChange }: ActivityFiltersProps) {
  const [selectedCategory, setSelectedCategory] = React.useState<string>('');
  const [selectedCountry, setSelectedCountry] = React.useState<string>('');
  const [citySearch, setCitySearch] = React.useState('');
  const [isExpanded, setIsExpanded] = React.useState(false);

  const activeFilterCount = [selectedCategory, selectedCountry, citySearch].filter(Boolean).length;

  const handleCategoryChange = (cat: string) => {
    setSelectedCategory(cat);
    onFilterChange({
      category: (cat || undefined) as ActivityCategory | undefined,
      countryCode: selectedCountry || undefined,
      city: citySearch || undefined,
    });
  };

  const handleCountryChange = (code: string) => {
    setSelectedCountry(code);
    onFilterChange({
      category: (selectedCategory || undefined) as ActivityCategory | undefined,
      countryCode: code || undefined,
      city: citySearch || undefined,
    });
  };

  const handleCityChange = (value: string) => {
    setCitySearch(value);
    onFilterChange({
      category: (selectedCategory || undefined) as ActivityCategory | undefined,
      countryCode: selectedCountry || undefined,
      city: value || undefined,
    });
  };

  const resetFilters = () => {
    setSelectedCategory('');
    setSelectedCountry('');
    setCitySearch('');
    onFilterChange({});
  };

  const activeSummary = [
    selectedCategory && ACTIVITY_CATEGORIES.find(c => c.id === selectedCategory)?.label,
    selectedCountry && budgetCountries.find(c => c.code === selectedCountry)?.name,
    citySearch,
  ].filter(Boolean);

  return (
    <div className="w-full">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200 ${
          isExpanded
            ? 'bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 rounded-b-none'
            : 'bg-white dark:bg-gray-800 shadow-sm border border-gray-200/80 dark:border-gray-700/50 hover:border-gray-300 dark:hover:border-gray-600'
        }`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`p-1.5 rounded-lg transition-colors ${activeFilterCount > 0 ? 'bg-teal-50 dark:bg-teal-900/20' : 'bg-gray-50 dark:bg-gray-700/50'}`}>
            <SlidersHorizontal className={`w-4 h-4 ${activeFilterCount > 0 ? 'text-teal-500' : 'text-gray-400 dark:text-gray-500'}`} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {activeFilterCount > 0 ? `${activeFilterCount} filtre${activeFilterCount > 1 ? 's' : ''}` : 'Filtrer'}
            </p>
            {activeSummary.length > 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                {activeSummary.join(' / ')}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 bg-teal-500 text-white text-[10px] font-bold rounded-full">
              {activeFilterCount}
            </span>
          )}
          {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="bg-white dark:bg-gray-800 border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-xl shadow-md p-4 sm:p-5">
          {activeFilterCount > 0 && (
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100 dark:border-gray-700/50">
              <div className="flex flex-wrap gap-1.5">
                {selectedCategory && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-md text-xs font-medium text-gray-600 dark:text-gray-300">
                    {ACTIVITY_CATEGORIES.find(c => c.id === selectedCategory)?.label}
                    <button onClick={() => handleCategoryChange('')} className="ml-0.5 hover:text-teal-500 transition-colors"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {selectedCountry && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-md text-xs font-medium text-gray-600 dark:text-gray-300">
                    <MapPin className="w-3 h-3" />
                    {budgetCountries.find(c => c.code === selectedCountry)?.name}
                    <button onClick={() => handleCountryChange('')} className="ml-0.5 hover:text-teal-500 transition-colors"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {citySearch && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-md text-xs font-medium text-gray-600 dark:text-gray-300">
                    {citySearch}
                    <button onClick={() => handleCityChange('')} className="ml-0.5 hover:text-teal-500 transition-colors"><X className="w-3 h-3" /></button>
                  </span>
                )}
              </div>
              <button onClick={resetFilters} className="text-xs font-medium text-gray-400 hover:text-teal-500 transition-colors flex-shrink-0 ml-2">
                Tout effacer
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Activité</label>
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="w-full px-3 py-2.5 sm:py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl sm:rounded-lg text-sm text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              >
                <option value="">Toutes</option>
                {ACTIVITY_CATEGORIES.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Pays</label>
              <select
                value={selectedCountry}
                onChange={(e) => handleCountryChange(e.target.value)}
                className="w-full px-3 py-2.5 sm:py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl sm:rounded-lg text-sm text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              >
                <option value="">Tous</option>
                {budgetCountries.map((c) => (
                  <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Ville</label>
              <input
                type="text"
                value={citySearch}
                onChange={(e) => handleCityChange(e.target.value)}
                placeholder="Ex: Montréal..."
                className="w-full px-3 py-2.5 sm:py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl sm:rounded-lg text-sm text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
